/**
 * Minimap drawing — derives a top-down picture of the settlement straight from
 * the same `WorldData` the 3D scene and the sim consume. No second source of
 * truth, no hand-authored minimap art to keep in sync.
 *
 * Two layers, deliberately split:
 *  - **static** (`drawMinimapBase`) — ground, objects, gate. Baked ONCE into an
 *    offscreen canvas by `bakeMinimapCanvas` and thereafter just blitted, so
 *    the 18 rotated rects are not re-rasterised at 60Hz.
 *  - **live** (`drawPlayerMarker`, `drawViewCone`) — player position/facing and
 *    the camera's heading, redrawn per frame on top of the blit.
 *
 * `bakeMinimapDataUrl` is the same static layer as a PNG data URL: the "bake a
 * picture" path, for shipping a hand-touched-up minimap as a static asset
 * (`WorldData.minimapImageUrl`) instead of deriving it at runtime.
 *
 * ── ORIENTATION (the thing most likely to be wrong) ──────────────────────────
 * World is XZ with Y up; `facing`/`rotationY` of 0 means +Z, and a positive
 * rotation about +Y maps (0,0,1) -> (sin t, 0, cos t).
 *
 * The canvas maps **world +X to canvas +x (right)** and **world +Z to canvas +y
 * (down)**. That is not an arbitrary pick: `camera-controller.ts` at yaw 0 sits
 * at target + (0, height, +horiz) looking toward -Z with +Y up, so on screen
 * right is +X and up is -Z. A north-locked minimap under this mapping therefore
 * matches the default camera framing exactly.
 *
 * Because canvas y runs *down* while the world's rotation sense is defined
 * around +Y, every world yaw becomes `-yaw` in canvas space (a world forward of
 * (sin t, cos t) equals a canvas `rotate(-t)` applied to a shape pointing at
 * +y). Getting that sign backwards mirrors the whole map — invisible wherever
 * the layout happens to be symmetric, which is exactly how the OBB collision
 * sign bug hid for so long. `projectToMinimap` and `canvasYaw` are pure and
 * unit-tested for that reason; see minimap-draw.test.ts.
 */
import type { WorldData, WorldObject } from '../types';

/** Offscreen bake resolution. Blitted down to whatever the widget displays at. */
export const MINIMAP_BAKE_SIZE = 512;

/** Per-`kind` palette — the map reads as a map, rather than as 18 arbitrary building colours. */
const KIND_FILL: Record<WorldObject['kind'], string> = {
  building: '#6b5a44',
  stall: '#8a5a34',
  gate: '#c99a4e',
  prop: '#3f352a',
  loot: '#d4a418', // S17 — Location worlds don't use this minimap, but the palette must stay exhaustive
  npc: '#c46a5a',
  grass: '#3a5c2e',
};

const GROUND_FILL = '#1a130c';
const GROUND_EDGE = 'rgba(122,90,48,0.55)';
const OBJECT_EDGE = 'rgba(20,13,7,0.65)';
const INTERACTABLE_DOT = '#ffdca6';
const PLAYER_FILL = '#ffb35c';
const PLAYER_EDGE = '#2b1d13';
const VIEW_CONE = 'rgba(255,179,92,0.14)';

/**
 * World XZ -> canvas pixels. Uniform scale (never squashes a non-square
 * ground), centred, so a `size`-square canvas holds the whole map.
 */
export function projectToMinimap(
  wx: number,
  wz: number,
  ground: { width: number; depth: number },
  size: number,
): { x: number; y: number } {
  const scale = minimapScale(ground, size);
  return {
    x: size / 2 + wx * scale,
    y: size / 2 + wz * scale,
  };
}

/** Pixels per world metre. */
export function minimapScale(ground: { width: number; depth: number }, size: number): number {
  const span = Math.max(ground.width, ground.depth);
  return span > 0 ? size / span : 1;
}

/**
 * World yaw -> canvas rotation. See the orientation note above: canvas y runs
 * down, so the sense of rotation flips.
 */
export function canvasYaw(worldYaw: number): number {
  return -worldYaw;
}

/** The static layer: ground plate + every object, each at its true rotation. */
export function drawMinimapBase(
  ctx: CanvasRenderingContext2D,
  world: WorldData,
  size: number,
): void {
  const { ground } = world;
  const scale = minimapScale(ground, size);

  ctx.clearRect(0, 0, size, size);

  // Ground plate, drawn at the ground's real extent rather than filling the
  // whole square — a non-square map must not imply walkable area it lacks.
  const gw = ground.width * scale;
  const gd = ground.depth * scale;
  ctx.fillStyle = GROUND_FILL;
  ctx.fillRect((size - gw) / 2, (size - gd) / 2, gw, gd);
  ctx.strokeStyle = GROUND_EDGE;
  ctx.lineWidth = Math.max(1, size / 256);
  ctx.strokeRect((size - gw) / 2, (size - gd) / 2, gw, gd);

  // Props first so buildings/stalls draw over them, not the reverse.
  const ordered = [...world.objects].sort((a, b) => rank(a.kind) - rank(b.kind));

  for (const obj of ordered) {
    const p = projectToMinimap(obj.position.x, obj.position.z, ground, size);
    // Collision overrides are the footprint the player actually bumps into,
    // so prefer them — a minimap that draws the *visual* box while the sim
    // enforces a different one is the visual-vs-truth mismatch class that
    // already bit this project once on rotated collision.
    const w = (obj.collisionW ?? obj.size.w) * scale;
    const d = (obj.collisionD ?? obj.size.d) * scale;

    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(canvasYaw(obj.rotationY ?? 0));
    ctx.fillStyle = KIND_FILL[obj.kind];
    ctx.fillRect(-w / 2, -d / 2, w, d);
    if (obj.kind !== 'prop') {
      ctx.strokeStyle = OBJECT_EDGE;
      ctx.lineWidth = Math.max(1, size / 512);
      ctx.strokeRect(-w / 2, -d / 2, w, d);
    }
    ctx.restore();

    // Talk-to markers sit unrotated on top — a dot has no orientation, and
    // rotating it would only move it off centre.
    if (obj.interactable) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(1.5, size / 150), 0, Math.PI * 2);
      ctx.fillStyle = INTERACTABLE_DOT;
      ctx.fill();
    }
  }
}

function rank(kind: WorldObject['kind']): number {
  return kind === 'prop' ? 0 : 1;
}

/** The live layer: player position + facing wedge. */
export function drawPlayerMarker(
  ctx: CanvasRenderingContext2D,
  world: WorldData,
  size: number,
  position: { x: number; z: number },
  facing: number,
): void {
  const p = projectToMinimap(position.x, position.z, world.ground, size);
  const r = Math.max(3, size / 60);

  ctx.save();
  ctx.translate(p.x, p.y);
  ctx.rotate(canvasYaw(facing));

  // Facing wedge points at +y in canvas space, which is world +Z — i.e. the
  // direction `facing === 0` means. The rotate() above carries it round.
  ctx.beginPath();
  ctx.moveTo(0, r * 2.1);
  ctx.lineTo(-r * 0.85, r * 0.6);
  ctx.lineTo(r * 0.85, r * 0.6);
  ctx.closePath();
  ctx.fillStyle = PLAYER_FILL;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fillStyle = PLAYER_FILL;
  ctx.fill();
  ctx.lineWidth = Math.max(1, size / 200);
  ctx.strokeStyle = PLAYER_EDGE;
  ctx.stroke();

  ctx.restore();
}

/**
 * The camera's heading, as a soft wedge behind the player marker. The minimap
 * is north-locked (the whole point of deriving it from static world data), so
 * without this the player has no cue tying the map's orientation to the freely
 * orbited camera.
 */
export function drawViewCone(
  ctx: CanvasRenderingContext2D,
  world: WorldData,
  size: number,
  position: { x: number; z: number },
  cameraYaw: number,
  radius: number,
  halfAngle = 0.55,
): void {
  const p = projectToMinimap(position.x, position.z, world.ground, size);
  // The camera sits at +(sin yaw, cos yaw) from its target and looks back at
  // it, so what the player is looking *at* is the opposite bearing.
  const look = canvasYaw(cameraYaw + Math.PI);
  // Canvas angles are measured from +x; the wedge's own zero is +y (world +Z).
  const base = look + Math.PI / 2;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(p.x, p.y);
  ctx.arc(p.x, p.y, radius, base - halfAngle, base + halfAngle);
  ctx.closePath();
  ctx.fillStyle = VIEW_CONE;
  ctx.fill();
  ctx.restore();
}

/** Bake the static layer into an offscreen canvas. Returns null if 2D is unavailable. */
export function bakeMinimapCanvas(world: WorldData, size = MINIMAP_BAKE_SIZE): HTMLCanvasElement | null {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  drawMinimapBase(ctx, world, size);
  return canvas;
}

/**
 * The same static layer as a PNG data URL — the "bake a picture" path. Save the
 * result under `frontend/public/` and point `WorldData.minimapImageUrl` at it
 * to serve a hand-touched-up minimap instead of the derived one.
 */
export function bakeMinimapDataUrl(world: WorldData, size = MINIMAP_BAKE_SIZE): string | null {
  const canvas = bakeMinimapCanvas(world, size);
  return canvas ? canvas.toDataURL('image/png') : null;
}
